import { useMemo, useRef, useState } from "react";

export type AccountOption = {
  readonly id: string;
  readonly name: string;
  readonly nickname?: string | null;
  readonly last4?: string | null;
};

const label = (o: AccountOption) =>
  `${o.nickname || o.name}${o.last4 ? ` ••${o.last4}` : ""}`;

// Type-to-search over the account's bank accounts with a create-on-the-fly
// option. Mirrors StakeholderCombobox: picking an existing one calls
// onPick({ bankAccountId }); creating calls onPick({ name }). Used by the rule
// account-filter editor and the create-rule internal-destination picker.
export default function AccountCombobox({
  options,
  onPick,
  autoFocus = true,
  placeholder = "Type an account…",
  error = null,
  allowCreate = true,
  alwaysShowCreate = false,
}: {
  options: ReadonlyArray<AccountOption>;
  onPick: (choice: { bankAccountId?: string; name?: string }) => void;
  autoFocus?: boolean;
  placeholder?: string;
  // Inline list-load / create failure surfaced under the input.
  error?: string | null;
  allowCreate?: boolean;
  // Keep the "create a new account" footer visible even before any text is typed, so the
  // create path is discoverable at all times (clicking it with an empty query focuses the
  // input to type the name). Default off — only the assign-to-internal-account surfaces want it.
  alwaysShowCreate?: boolean;
}) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const inputRef = useRef<HTMLInputElement>(null);

  // Show the full account set (it's small — a handful of bank + card accounts) so a
  // card account, which is a valid internal destination, isn't hidden below the fold.
  // The list scrolls (max-h-64); typing narrows it.
  const LIST_CAP = 50;
  const matches = useMemo(() => {
    if (!trimmed) return options.slice(0, LIST_CAP);
    const needle = trimmed.toLowerCase();
    return options
      .filter((o) => label(o).toLowerCase().includes(needle))
      .slice(0, LIST_CAP);
  }, [options, trimmed]);

  const exactExists = options.some(
    (o) => label(o).toLowerCase() === trimmed.toLowerCase()
  );

  return (
    <div className="w-72 text-sm">
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded border px-2 py-1.5 text-sm"
      />
      {error && <p className="mt-1 px-1 text-xs text-red-600">{error}</p>}
      <div className="mt-1 max-h-64 overflow-y-auto">
        {matches.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onPick({ bankAccountId: option.id })}
            className="block w-full text-left px-2 py-1.5 hover:bg-gray-50 rounded"
          >
            {label(option)}
          </button>
        ))}
        {allowCreate && !exactExists && (trimmed || alwaysShowCreate) && (
          <button
            type="button"
            onClick={() => (trimmed ? onPick({ name: trimmed }) : inputRef.current?.focus())}
            className="block w-full text-left px-2 py-1.5 font-medium text-emerald-700 hover:bg-emerald-50 rounded border-t"
          >
            {trimmed ? `+ create "${trimmed}"` : "+ Create a new account…"}
          </button>
        )}
        {matches.length === 0 && !trimmed && (
          <p className="px-2 py-1.5 text-gray-400">Start typing to search…</p>
        )}
      </div>
    </div>
  );
}
